import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const EMPTY_FORM = {
  name: '', description: '', price_amount: '',
  billing_type: 'subscription', billing_interval: 'month',
  gst_enabled: true, provincial_tax_rate: '', provincial_tax_name: ''
};

const INTERVAL_LABELS = {
  week: 'Weekly', biweek: 'Bi-weekly', month: 'Monthly', year: 'Yearly'
};

const GST_RATE = 0.05; // 5% federal GST

export default function CoachProducts() {
  const { profile } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tab, setTab] = useState('active');

  useEffect(() => {
    if (profile?.id) fetchProducts();
  }, [profile]);

  const fetchProducts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('products')
      .select('*')
      .eq('coach_id', profile.id)
      .order('created_at', { ascending: false });
    setProducts(data || []);
    setLoading(false);
  };

  const openNew = () => {
    setEditingProduct(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingProduct(p);
    setForm({
      name: p.name,
      description: p.description || '',
      price_amount: p.price_amount,
      billing_type: p.billing_type,
      billing_interval: p.billing_interval || 'month',
      gst_enabled: p.gst_enabled !== false,
      provincial_tax_rate: p.provincial_tax_rate || '',
      provincial_tax_name: p.provincial_tax_name || ''
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        coach_id: profile.id,
        name: form.name,
        description: form.description,
        price_amount: parseFloat(form.price_amount),
        billing_type: form.billing_type,
        billing_interval: form.billing_type === 'subscription' ? form.billing_interval : null,
        currency: 'cad',
        status: 'active',
        gst_enabled: form.gst_enabled,
        provincial_tax_rate: form.provincial_tax_rate ? parseFloat(form.provincial_tax_rate) : 0,
        provincial_tax_name: form.provincial_tax_name || null
      };

      if (editingProduct) {
        const { error: e } = await supabase.from('products').update(payload).eq('id', editingProduct.id).eq('coach_id', profile.id);
        if (e) throw e;
      } else {
        const { error: e } = await supabase.from('products').insert(payload);
        if (e) throw e;
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (id) => {
    await supabase.from('products').update({ status: 'archived' }).eq('id', id).eq('coach_id', profile.id);
    fetchProducts();
  };

  const handleRestore = async (id) => {
    await supabase.from('products').update({ status: 'active' }).eq('id', id).eq('coach_id', profile.id);
    fetchProducts();
  };

  const handleDelete = async (id) => {
    await supabase.from('products').delete().eq('id', id).eq('coach_id', profile.id);
    setConfirmDelete(null);
    fetchProducts();
  };

  const handleCopyPaymentLink = async (product) => {
    if (product.stripe_payment_link) {
      copyLink(product.stripe_payment_link, product.id);
      return;
    }
    setGeneratingLink(product.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/products/${product.id}/payment-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        await supabase.from('products').update({ stripe_payment_link: data.url }).eq('id', product.id);
        copyLink(data.url, product.id);
        fetchProducts();
      } else {
        const data = await res.json();
        alert('Error: ' + (data.error || 'Could not generate payment link'));
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setGeneratingLink(null);
    }
  };

  const copyLink = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2500);
    });
  };

  // Calculate price breakdown
  const calcPrices = (price, gstEnabled, provincialRate) => {
    const subtotal = parseFloat(price) || 0;
    const gst = gstEnabled ? subtotal * GST_RATE : 0;
    const provincial = provincialRate ? subtotal * (parseFloat(provincialRate) / 100) : 0;
    const total = subtotal + gst + provincial;
    return { subtotal, gst, provincial, total };
  };

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const activeProducts = products.filter(p => p.status === 'active');
  const archivedProducts = products.filter(p => p.status === 'archived');
  const displayed = tab === 'active' ? activeProducts : archivedProducts;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 32, marginBottom: 8 }}>Products & Services</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Create products and share payment links with clients</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ New Product</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setTab('active')} className={tab === 'active' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
          Active ({activeProducts.length})
        </button>
        <button onClick={() => setTab('archived')} className={tab === 'archived' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
          Archived ({archivedProducts.length})
        </button>
      </div>

      {loading ? <div style={{ color: 'var(--text-secondary)' }}>Loading...</div> : (
        displayed.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 12 }}>
            {tab === 'active' ? 'No active products yet. Create your first service above.' : 'No archived products.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {displayed.map(p => {
              const prices = calcPrices(p.price_amount, p.gst_enabled, p.provincial_tax_rate);
              return (
                <div key={p.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 16 }}>{p.name}</span>
                      <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 20, background: 'rgba(201,169,110,0.12)', color: 'var(--accent)' }}>
                        {p.billing_type === 'one_time' ? 'One-time' : INTERVAL_LABELS[p.billing_interval]}
                      </span>
                      {p.gst_enabled && (
                        <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 20, background: 'rgba(76,175,130,0.12)', color: 'var(--success)' }}>
                          +GST
                        </span>
                      )}
                      {p.provincial_tax_rate > 0 && (
                        <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 20, background: 'rgba(85,153,221,0.12)', color: 'var(--info)' }}>
                          +{p.provincial_tax_name || 'PST'}
                        </span>
                      )}
                    </div>
                    {p.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>{p.description}</div>}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <div style={{ fontSize: 22, fontFamily: 'Playfair Display, serif', color: 'var(--accent)' }}>
                        {fmt(p.price_amount)} <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>CAD</span>
                      </div>
                      {(p.gst_enabled || p.provincial_tax_rate > 0) && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          Client pays {fmt(prices.total)} total
                          {p.gst_enabled && ` (incl. ${fmt(prices.gst)} GST)`}
                          {p.provincial_tax_rate > 0 && ` + ${fmt(prices.provincial)} ${p.provincial_tax_name || 'PST'}`}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
                    {tab === 'active' && (
                      <>
                        <button className="btn btn-primary btn-sm" style={{ minWidth: 140 }}
                          onClick={() => handleCopyPaymentLink(p)}
                          disabled={generatingLink === p.id}>
                          {generatingLink === p.id ? 'Generating...' : copiedId === p.id ? '✓ Copied!' : '🔗 Payment Link'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>Edit</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleArchive(p.id)}>Archive</button>
                      </>
                    )}
                    {tab === 'archived' && (
                      <>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleRestore(p.id)}>Restore</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(p)}>Delete</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h3 style={{ fontSize: 20, marginBottom: 12 }}>Delete Product?</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>
              Permanently delete <strong>{confirmDelete.name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-danger" onClick={() => handleDelete(confirmDelete.id)} style={{ flex: 1, justifyContent: 'center' }}>Delete</button>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)} style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ fontSize: 20 }}>{editingProduct ? 'Edit Product' : 'New Product'}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="label">Product Name</label>
                <input className="input" placeholder="e.g. Monthly Coaching Package"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="label">Description</label>
                <textarea className="input" rows={2} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="label">Price (CAD, before tax)</label>
                  <input type="number" step="0.01" min="0" className="input" placeholder="199.00"
                    value={form.price_amount} onChange={e => setForm(f => ({ ...f, price_amount: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="label">Billing Type</label>
                  <select className="input" value={form.billing_type} onChange={e => setForm(f => ({ ...f, billing_type: e.target.value }))}>
                    <option value="subscription">Subscription</option>
                    <option value="one_time">One-time</option>
                  </select>
                </div>
              </div>
              {form.billing_type === 'subscription' && (
                <div className="form-group">
                  <label className="label">Billing Interval</label>
                  <select className="input" value={form.billing_interval} onChange={e => setForm(f => ({ ...f, billing_interval: e.target.value }))}>
                    <option value="week">Weekly</option>
                    <option value="biweek">Bi-weekly (every 2 weeks)</option>
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </select>
                </div>
              )}

              {/* Tax Settings */}
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Tax Settings
                </div>

                {/* GST Toggle */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>GST (5% Federal)</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Applies across Canada</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: form.gst_enabled ? 'var(--success)' : 'var(--text-muted)' }}>
                      {form.gst_enabled ? 'On' : 'Off'}
                    </span>
                    <div
                      onClick={() => setForm(f => ({ ...f, gst_enabled: !f.gst_enabled }))}
                      style={{
                        width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
                        background: form.gst_enabled ? 'var(--success)' : 'var(--border)',
                        position: 'relative', transition: 'background 0.2s'
                      }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%', background: 'white',
                        position: 'absolute', top: 3,
                        left: form.gst_enabled ? 23 : 3,
                        transition: 'left 0.2s'
                      }} />
                    </div>
                  </div>
                </div>

                {/* Provincial Tax */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="label">Provincial Tax Rate % (optional)</label>
                    <input type="number" step="0.1" min="0" max="20" className="input"
                      placeholder="e.g. 7 for BC PST"
                      value={form.provincial_tax_rate}
                      onChange={e => setForm(f => ({ ...f, provincial_tax_rate: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="label">Tax Name</label>
                    <select className="input" value={form.provincial_tax_name}
                      onChange={e => setForm(f => ({ ...f, provincial_tax_name: e.target.value }))}>
                      <option value="">None</option>
                      <option value="PST">PST</option>
                      <option value="HST">HST (provincial portion)</option>
                      <option value="QST">QST</option>
                      <option value="RST">RST</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Price preview with tax */}
              {form.price_amount && (
                <div style={{
                  background: 'rgba(201,169,110,0.06)', border: '1px solid rgba(201,169,110,0.15)',
                  borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13
                }}>
                  <div style={{ fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Price Breakdown</div>
                  {(() => {
                    const prices = calcPrices(form.price_amount, form.gst_enabled, form.provincial_tax_rate);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>
                          <span>{fmt(prices.subtotal)}</span>
                        </div>
                        {form.gst_enabled && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'var(--text-muted)' }}>GST (5%)</span>
                            <span>{fmt(prices.gst)}</span>
                          </div>
                        )}
                        {prices.provincial > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{form.provincial_tax_name || 'Provincial'} ({form.provincial_tax_rate}%)</span>
                            <span>{fmt(prices.provincial)}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 4 }}>
                          <span style={{ fontWeight: 600 }}>Client pays</span>
                          <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{fmt(prices.total)} CAD</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={saving}>
                {saving ? 'Saving...' : editingProduct ? 'Save Changes' : 'Create Product'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
